import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, ShieldCheck, Sparkles, TrendingUp, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { buildWhatsAppLink } from "@/lib/whatsapp";
import TID from "@/lib/testIds";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.4 },
};

export const Hero = () => {
  return (
    <section
      id="home"
      data-testid={TID.heroSection}
      className="relative pt-36 pb-24 md:pt-44 md:pb-32 overflow-hidden"
    >
      {/* Animated blobs */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-brand-blue/30 blur-3xl animate-blob-float" />
        <div className="absolute top-20 -right-32 w-[460px] h-[460px] rounded-full bg-brand-purple/25 blur-3xl animate-blob-float [animation-delay:-6s]" />
        <div className="absolute bottom-0 left-1/3 w-[380px] h-[380px] rounded-full bg-sky-400/20 blur-3xl animate-blob-float [animation-delay:-12s]" />
        <div className="absolute inset-0 bg-grid-soft opacity-50" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center relative">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full glass text-xs md:text-sm font-medium text-slate-700 dark:text-slate-200"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-blue opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-blue" />
            </span>
            Security · Performance · Growth
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-6 font-display font-bold tracking-tight text-slate-900 dark:text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05]"
          >
            Protect Your Business.{" "}
            <span className="text-gradient-brand">Grow Your Brand.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 text-base md:text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-2xl"
          >
            Professional Penetration Testing and data-driven Digital Marketing
            that helps businesses stay secure while scaling faster.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-9 flex flex-wrap gap-3"
          >
            <Button
              data-testid={TID.heroPrimaryCta}
              onClick={() => scrollTo("contact")}
              className="h-12 px-7 rounded-full bg-gradient-to-r from-brand-blue to-brand-purple text-white font-semibold shadow-elevated hover:opacity-95 btn-ripple group"
            >
              Book Free Consultation
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={TID.heroSecondaryCta}
              className="h-12 inline-flex items-center px-7 rounded-full glass font-semibold text-slate-800 dark:text-white border border-slate-200 dark:border-white/10 hover:bg-white dark:hover:bg-white/10 transition"
            >
              <MessageCircle className="w-4 h-4 mr-2 text-emerald-500" />
              Chat on WhatsApp
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-slate-500 dark:text-slate-400"
          >
            <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-brand-blue" />OWASP Aligned</span>
            <span className="inline-flex items-center gap-2"><Lock className="w-4 h-4 text-brand-purple" />NDA & Confidential</span>
            <span className="inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" />Award-winning craft</span>
          </motion.div>
        </div>

        {/* Visual */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[5/6] w-full max-w-md mx-auto">
            {/* Floating card 1 — Security */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-4 left-2 right-4 rounded-3xl p-6 glass shadow-elevated"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-brand-blue/10 text-brand-blue inline-flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5" />
                  </span>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Security Score</p>
                    <p className="font-display text-xl font-bold text-slate-900 dark:text-white">A+ · 98/100</p>
                  </div>
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-600 font-semibold">Secure</span>
              </div>
              <div className="mt-4 h-2 w-full rounded-full bg-slate-200 dark:bg-white/10 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: "98%" }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.4, ease: "easeOut" }}
                  className="h-full bg-gradient-to-r from-brand-blue to-brand-purple"
                />
              </div>
            </motion.div>

            {/* Floating card 2 — Growth */}
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
              className="absolute bottom-2 right-0 w-[78%] rounded-3xl p-6 glass shadow-elevated"
            >
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-brand-purple/10 text-brand-purple inline-flex items-center justify-center">
                  <TrendingUp className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Campaign ROAS</p>
                  <p className="font-display text-xl font-bold text-slate-900 dark:text-white">+412%</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-7 items-end gap-1.5 h-16">
                {[12, 24, 18, 36, 28, 48, 64].map((v, i) => (
                  <motion.span
                    key={i}
                    initial={{ height: 0 }}
                    whileInView={{ height: `${v + 8}%` }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 * i, duration: 0.6 }}
                    className="rounded bg-gradient-to-t from-brand-blue/40 to-brand-purple/80"
                    style={{ minHeight: 6 }}
                  />
                ))}
              </div>
            </motion.div>

            {/* Center medallion */}
            <motion.div
              animate={{ rotate: [0, 8, 0, -8, 0] }}
              transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-12 rounded-full border border-dashed border-brand-blue/30"
            />
            <motion.div
              animate={{ rotate: [360, 0] }}
              transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
              className="absolute inset-20 rounded-full border border-dashed border-brand-purple/30"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-28 h-28 rounded-3xl bg-gradient-to-br from-brand-blue to-brand-purple shadow-elevated flex items-center justify-center">
                <ShieldCheck className="w-14 h-14 text-white" strokeWidth={1.6} />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
